import * as moment from 'moment';
import * as _ from 'lodash';
import { useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { IWatrRequest, IWatrReimbursement, IListItem, IUser, ApprovalEmailContract } from '@nkr/models/index';
import { WageAndTravelType, WageAndTravelStatus, WageAndTravelReimbursementRequestStatus, SendEmail } from '@nkr/enums/index';
import { FormControl, Row, FormGroup, ControlLabel, Col, Checkbox, Button, OverlayTrigger, Popover, Panel, Glyphicon, Modal } from 'react-bootstrap';
import { downloadFile } from '@nkr/services/download-file';
import { FileDocumentType } from '@nkr/enums/document-type';
import { DonorShieldContext } from './context';
import { toast } from 'react-toastify';
import WatrInfoAccordions from './accordion';
import Header from './header';
import WageSummaryShared from './common/wage-summary-shared';
import { hasAnyWageData } from './common/wage-computation';
import { CompactBankingRow } from './common/compact-banking-row';
import './verify/components/reimbursement-verify.css';

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    onUpdateAudit: (requestId: number, type: WageAndTravelType, status: WageAndTravelStatus
        , reimbursementAmount: number, donationDate: string, incomePercentCovered: number
        , sixtyDayOverride: boolean, ninetyDayLWROverride: boolean, expenseLimitOverride: boolean) => void,
}

type AuditFormSnapshot = {
    status: number;
    donationDate: string;
    reimbursementType: number;
    reimbursementAmount: number;
    sixtyDayOverride: boolean;
    ninetyDayLWROverride: boolean;
    expenseLimitOverride: boolean;
};

const isTerminalWatrStatus = (status: number) => status >= WageAndTravelStatus.Denied;

const getPendingAdditionalWeeks = (data: IWatrRequest): number[] => {
    if (!data || !data.requestWeeks) {
        return [];
    }
    return data.requestWeeks
        .filter(w =>
            w.status === WageAndTravelReimbursementRequestStatus.PendingApproval
            && w.weekRequested > 3)
        .map(w => w.weekRequested)
        .sort((a, b) => a - b);
};

const auditWageAmountFromData = (data: IWatrRequest) =>
    Number(data.actualWageReimbursementAmount ?? data.wageReimbursementAmount ?? data.estimatedWageReimbursementAmount ?? 0);

const auditDonationDateFromData = (data: IWatrRequest) =>
    moment(data.donationDate).isValid() ? moment(data.donationDate).format('YYYY-MM-DD') : '';

const buildAuditSnapshotFromData = (data: IWatrRequest): AuditFormSnapshot => ({
    status: Number(data.status),
    donationDate: auditDonationDateFromData(data),
    reimbursementType: Number(data.watrRequestType),
    reimbursementAmount: auditWageAmountFromData(data),
    sixtyDayOverride: !!data.sixtyDayOverride,
    ninetyDayLWROverride: !!data.ninetyDayLWROverride,
    expenseLimitOverride: !!data.expenseLimitOverride
});

const buildAuditSnapshotFromForm = (
    status: number,
    donationDate: string,
    reimbursementType: number,
    reimbursementAmount: number,
    sixtyDayOverride: boolean,
    ninetyDayLWROverride: boolean,
    expenseLimitOverride: boolean
): AuditFormSnapshot => ({
    status: Number(status),
    donationDate: donationDate || '',
    reimbursementType: Number(reimbursementType),
    reimbursementAmount: Number.isFinite(Number(reimbursementAmount)) ? Number(reimbursementAmount) : 0,
    sixtyDayOverride: !!sixtyDayOverride,
    ninetyDayLWROverride: !!ninetyDayLWROverride,
    expenseLimitOverride: !!expenseLimitOverride
});

const auditSnapshotsEqual = (a: AuditFormSnapshot, b: AuditFormSnapshot) =>
    a.status === b.status
    && a.donationDate === b.donationDate
    && a.reimbursementType === b.reimbursementType
    && Math.abs(a.reimbursementAmount - b.reimbursementAmount) < 0.005
    && a.sixtyDayOverride === b.sixtyDayOverride
    && a.ninetyDayLWROverride === b.ninetyDayLWROverride
    && a.expenseLimitOverride === b.expenseLimitOverride;

type AuditAdjustmentsSummaryField = {
    id: keyof AuditFormSnapshot | 'overrides';
    label: string;
    value: string;
};

const formatAuditDonationDateDisplay = (donationDate: string) => {
    if (!donationDate) {
        return '—';
    }
    const parsed = moment(donationDate, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : donationDate;
};

const buildAuditAdjustmentsSummaryFields = (
    requestTypes: IListItem[],
    statusTypes: IListItem[],
    form: AuditFormSnapshot
): AuditAdjustmentsSummaryField[] => {
    const requestTypeLabel = requestTypes.find(x => x.id === form.reimbursementType)?.description ?? '—';
    const statusLabel = statusTypes.find(x => x.id === form.status)?.description ?? '—';
    const wageFormatted = Number.isFinite(form.reimbursementAmount)
        ? `$${form.reimbursementAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        : '—';

    const activeOverrides: string[] = [];
    if (form.ninetyDayLWROverride) {
        activeOverrides.push('90-day wages');
    }
    if (form.sixtyDayOverride) {
        activeOverrides.push('60-day travel');
    }
    if (form.expenseLimitOverride) {
        activeOverrides.push('Multi DER');
    }

    return [
        { id: 'reimbursementType', label: 'Request Type', value: requestTypeLabel },
        { id: 'donationDate', label: 'Donation Date', value: formatAuditDonationDateDisplay(form.donationDate) },
        { id: 'status', label: 'Status', value: statusLabel },
        { id: 'reimbursementAmount', label: 'Wage Amount', value: wageFormatted },
        {
            id: 'overrides',
            label: 'Policy Overrides',
            value: activeOverrides.length > 0 ? activeOverrides.join(' · ') : 'None enabled'
        }
    ];
};

const isAuditSummaryFieldChanged = (
    fieldId: AuditAdjustmentsSummaryField['id'],
    form: AuditFormSnapshot,
    saved: AuditFormSnapshot | null
) => {
    if (!saved) {
        return false;
    }
    if (fieldId === 'overrides') {
        return form.sixtyDayOverride !== saved.sixtyDayOverride
            || form.ninetyDayLWROverride !== saved.ninetyDayLWROverride
            || form.expenseLimitOverride !== saved.expenseLimitOverride;
    }
    if (fieldId === 'reimbursementAmount') {
        return Math.abs(form.reimbursementAmount - saved.reimbursementAmount) >= 0.005;
    }
    return form[fieldId] !== saved[fieldId];
};

const renderPolicyOverrideChip = (
    active: boolean,
    onToggle: () => void,
    label: string,
    popoverId: string,
    popoverTitle: string,
    popoverBody: string
) => (
    <div style={{ position: 'relative', flex: '1 1 140px', minWidth: 120 }}>
        <Button
            bsStyle='default'
            onClick={onToggle}
            style={{
                width: '100%',
                padding: '5px 26px 5px 8px',
                textAlign: 'left',
                borderRadius: 6,
                borderColor: active ? '#3c763d' : '#d7dee6',
                background: active ? '#e8f8f0' : '#fff',
                fontSize: 12,
                lineHeight: 1.3
            }}
        >
            <span style={{ fontWeight: 600, color: active ? '#2e6b32' : '#1f2d3d' }}>{label}</span>
            <span style={{ marginLeft: 6, color: active ? '#2e6b32' : '#7a8794' }}>{active ? 'On' : 'Off'}</span>
        </Button>
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement='top'
            overlay={
                <Popover id={popoverId} title={popoverTitle}>
                    {popoverBody}
                </Popover>
            }
        >
            <span
                role='button'
                tabIndex={0}
                style={{
                    position: 'absolute',
                    top: 6,
                    right: 8,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '1px solid #b9c4cf',
                    color: '#607182',
                    background: '#fff',
                    cursor: 'help',
                    fontWeight: 700,
                    fontSize: 10,
                    lineHeight: '14px',
                    textAlign: 'center'
                }}
            >
                i
            </span>
        </OverlayTrigger>
    </div>
);

const wageAmountAuditHelp = (
    <div className='audit-field-help-popover'>
        <p><strong>What it is:</strong> The stored dollar total for lost wages (LWR) on this request.</p>
        <p><strong>Travel:</strong> Does not change travel reimbursement (TLR/DER). Wages only.</p>
        <p><strong>If you see $0:</strong> Usually pending — wage verify has not written a total yet. Do not guess. Leave $0 and use Status / verify unless you are fixing bad data on the record.</p>
        <p><strong>When to change it:</strong> Data correction only. Approval happens in verify, not here.</p>
    </div>
);

const renderAuditFieldLabelWithInfo = (
    label: string,
    popoverId: string,
    popoverTitle: string,
    popoverBody: ReactNode
) => (
    <div className='audit-field-label-row'>
        <ControlLabel className='audit-field-label'>{label}</ControlLabel>
        <OverlayTrigger
            trigger={['hover', 'focus']}
            placement='top'
            overlay={
                <Popover id={popoverId} title={popoverTitle}>
                    {popoverBody}
                </Popover>
            }
        >
            <span className='audit-field-info-icon' role='button' tabIndex={0}>
                i
            </span>
        </OverlayTrigger>
    </div>
);

const ReimburseWages = (props: IProps) => {
    const [ninetyDayLWROverride, setNinetyDayLWROverride] = useState(false);
    const [sixtyDayOverride, setSixtyDayOverride] = useState(false);
    const [expenseLimitOverride, setExpenseLimitOverride] = useState(false);
    const [reimbursementAmount, setReimbursementAmount] = useState(0);
    const [reimbursementDate, setReimbursementDate] = useState('');
    const [reimbursementType, setReimbursementType] = useState(0);
    const [confirmation, setConfirmation] = useState('');
    const [status, setStatus] = useState(WageAndTravelStatus.PendingDocuments);
    const [donationDate, setDonationDate] = useState('');
    const [sendNotice, setSendNotice] = useState(true);
    const [incomePercentCovered, setIncomePercentCovered] = useState(0);
    const [savingWeekRequested, setSavingWeekRequested] = useState<number | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [bankingRevealed, setBankingRevealed] = useState(false);
    const [auditAdjustmentsOpen, setAuditAdjustmentsOpen] = useState(false);
    const [auditSavedSnapshot, setAuditSavedSnapshot] = useState<AuditFormSnapshot | null>(null);
    const [showTerminalStatusConfirm, setShowTerminalStatusConfirm] = useState(false);

    const context = useContext(DonorShieldContext);

    const canEditOtherAmount = context.isNkrSuper && !!context.onUpdateRequestWeekOtherAmount;

    const handleSaveWeekOtherAmount = async (weekRequested: number, otherAmount: number) => {
        if (!context.onUpdateRequestWeekOtherAmount) return;
        setSavingWeekRequested(weekRequested);
        setSaveError(null);
        try {
            const response = await context.onUpdateRequestWeekOtherAmount(props.data.id, weekRequested, otherAmount);
            const res = response as any;
            if (!res?.data?.success && res?.data?.message) {
                setSaveError(res.data.message);
                toast.error(res.data.message);
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Failed to save other amount.';
            setSaveError(msg);
            toast.error(msg);
        } finally {
            setSavingWeekRequested(null);
        }
    };

    useEffect(() => {
        const snap = buildAuditSnapshotFromData(props.data);
        setStatus(snap.status);
        setDonationDate(snap.donationDate);
        setReimbursementType(snap.reimbursementType);
        setReimbursementAmount(snap.reimbursementAmount);
        setIncomePercentCovered(props.data.incomePercentCovered);
        setSixtyDayOverride(snap.sixtyDayOverride);
        setNinetyDayLWROverride(snap.ninetyDayLWROverride);
        setExpenseLimitOverride(snap.expenseLimitOverride);
        setAuditSavedSnapshot(snap);
    }, [props.data]);

    useEffect(() => {
        setBankingRevealed(false);
        setAuditAdjustmentsOpen(false);
    }, [props.data.id]);

    const getDonationType = (donationType) => {
        let type = context.donationTypes.find(x => x.id == donationType);

        if (type == null)
            return "Unknown";
        return type.abbreviation;
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let { name, value } = e.currentTarget;

        if (name == 'status') {
            setStatus(parseInt(value));
        }

        if (name == 'donationDate') {
            setDonationDate(value);
        }

        if (name == 'reimbursementType') {
            setReimbursementType(parseInt(value));
        }

        if (name == 'reimbursementAmount') {
            setReimbursementAmount(parseFloat(value));
        }
    };

    const handleVerifyAmount = (status: WageAndTravelStatus, sendEmail: boolean) => {
        context.onVerifyAmount(props.data.id, status, sendNotice, reimbursementAmount, sendEmail);
    };

    const pendingAdditionalWeeks = useMemo(() => getPendingAdditionalWeeks(props.data), [props.data]);

    const willAutoDeclinePendingWeeks =
        isTerminalWatrStatus(status)
        && !isTerminalWatrStatus(Number(props.data.status))
        && pendingAdditionalWeeks.length > 0;

    const submitAuditUpdate = () => {
        props.onUpdateAudit(props.data.id, reimbursementType, status, reimbursementAmount, donationDate, incomePercentCovered, sixtyDayOverride, ninetyDayLWROverride, expenseLimitOverride);
        setShowTerminalStatusConfirm(false);
    };

    const handleAuditUpdate = () => {
        if (willAutoDeclinePendingWeeks) {
            setShowTerminalStatusConfirm(true);
            return;
        }
        submitAuditUpdate();
    };

    const handleDownloadFile = (docType: FileDocumentType, lookupId: number) => {
        context.downloadFile(docType, lookupId)
            .then((response: any) => {

                let fileName = props.data.firstName + '_' + props.data.lastName
                    + "_" + FileDocumentType[docType].toString()
                    + "_" + moment().format("YYYY_MM_DD");

                downloadFile(response.data, fileName, response.data.type);
            })
            .catch((e: any) => {
                const msg = e?.response?.data?.message || e?.message || 'Unable to download file.';
                toast.error(msg);
            });
    }

    const isAuditAndNkrVerifyTax = props.isAudit && props.data.status == WageAndTravelStatus.PendingNkrVerifyTax;
    const hasWageData = hasAnyWageData(props.data, reimbursementAmount);

    const auditFormSnapshot = useMemo(() => buildAuditSnapshotFromForm(
        status,
        donationDate,
        reimbursementType,
        reimbursementAmount,
        sixtyDayOverride,
        ninetyDayLWROverride,
        expenseLimitOverride
    ), [
        status,
        donationDate,
        reimbursementType,
        reimbursementAmount,
        sixtyDayOverride,
        ninetyDayLWROverride,
        expenseLimitOverride
    ]);

    const hasPendingAuditChanges = props.isAudit
        && auditSavedSnapshot !== null
        && !auditSnapshotsEqual(auditSavedSnapshot, auditFormSnapshot);

    const auditSummaryFields = useMemo(
        () => buildAuditAdjustmentsSummaryFields(context.requestTypes, context.statusTypes, auditFormSnapshot),
        [context.requestTypes, context.statusTypes, auditFormSnapshot]
    );

    const openAuditAdjustments = () => setAuditAdjustmentsOpen(true);

    return (
        <div>
            <Header data={props.data} showStatus={false} />

            <div className='reimbursement-verify' style={{ marginTop: 10 }}>
                <div className='page-wrap'>
                    <CompactBankingRow
                        data={props.data}
                        bankingRevealed={bankingRevealed}
                        onToggleBankingRevealed={() => setBankingRevealed(v => !v)}
                        variant="boxed"
                        showPaymentLabel={false}
                    />
                    <WageSummaryShared
                        data={props.data}
                        mode='readOnly'
                        wageReimbursementAmount={reimbursementAmount}
                        showWeekByWeekBreakdown={false}
                        showWageVerificationDocuments={hasWageData}
                        onDownloadWageVerificationDocument={(docType) => handleDownloadFile(docType, props.data.id)}
                        showWageAmountPill={!props.isAudit}
                        wageAmountPillAmount={props.data.actualWageReimbursementAmount}
                    />
                </div>
                {props.isAudit &&
                    <Panel
                        className='panel audit-adjustments-panel reimbursement-verify'
                        expanded={auditAdjustmentsOpen}
                        onToggle={() => setAuditAdjustmentsOpen(open => !open)}
                    >
                        <Panel.Heading className='audit-adjustments-panel-heading' style={{ paddingTop: 5, paddingBottom: 5 }}>
                            <Panel.Title toggle className='audit-adjustments-panel-title' style={{ fontSize: 14 }}>
                                <Glyphicon
                                    glyph={auditAdjustmentsOpen ? 'chevron-up' : 'chevron-down'}
                                    className='audit-adjustments-chevron'
                                />
                                Audit Adjustments
                                {hasPendingAuditChanges &&
                                    <span className='audit-adjustments-pending-badge'>Pending Changes</span>
                                }
                            </Panel.Title>
                            {!auditAdjustmentsOpen &&
                                <div
                                    className='audit-adjustments-collapsed-summary'
                                    role='button'
                                    tabIndex={0}
                                    onClick={openAuditAdjustments}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openAuditAdjustments();
                                        }
                                    }}
                                    aria-label='Expand Audit Adjustments to edit'
                                >
                                    <div className='audit-adjustments-summary-grid'>
                                        {auditSummaryFields.map(field => {
                                            const isChanged = isAuditSummaryFieldChanged(
                                                field.id,
                                                auditFormSnapshot,
                                                auditSavedSnapshot
                                            );
                                            return (
                                                <div
                                                    key={field.id}
                                                    className={`audit-adjustments-summary-item${isChanged ? ' is-changed' : ''}`}
                                                >
                                                    <span className='audit-adjustments-summary-label'>{field.label}</span>
                                                    <span className='audit-adjustments-summary-value' title={field.value}>
                                                        {field.value}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <span className='audit-adjustments-summary-edit-hint'>Click to Edit</span>
                                </div>
                            }
                        </Panel.Heading>
                        <Panel.Body collapsible className='panel-body audit-adjustments-body'>
                            <p className='audit-adjustments-intro'>
                                Super-user corrections to this request record.
                            </p>
                            <div className='audit-adjustments-request-grid'>
                                <div className='audit-field audit-field-span-wide audit-field-request-type'>
                                    <ControlLabel className='audit-field-label'>Request Type</ControlLabel>
                                    <FormControl
                                        componentClass='select'
                                        name='reimbursementType'
                                        value={reimbursementType}
                                        onChange={handleInputChange}
                                        className='audit-field-control'
                                        title={
                                            context.requestTypes.find(x => x.id === reimbursementType)?.description
                                        }
                                    >
                                        {context.requestTypes.map(item => (
                                            <option value={item.id} key={item.id}>{item.description}</option>
                                        ))}
                                    </FormControl>
                                </div>
                                <div className='audit-field'>
                                    <ControlLabel className='audit-field-label'>Donation Date</ControlLabel>
                                    <FormControl
                                        type='date'
                                        id='date'
                                        name='donationDate'
                                        value={donationDate}
                                        onChange={handleInputChange}
                                        className='audit-field-control'
                                    />
                                </div>
                                <div className='audit-field'>
                                    <ControlLabel className='audit-field-label'>Status</ControlLabel>
                                    <FormControl
                                        componentClass='select'
                                        name='status'
                                        value={status}
                                        onChange={handleInputChange}
                                        className='audit-field-control'
                                        title={context.statusTypes.find(x => x.id === status)?.description}
                                    >
                                        {context.statusTypes.map(item => (
                                            <option value={item.id} key={item.id}>{item.description}</option>
                                        ))}
                                    </FormControl>
                                </div>
                                <div className='audit-field audit-field-wage'>
                                    {renderAuditFieldLabelWithInfo(
                                        'Wage Amount',
                                        'popover-audit-wage-amount',
                                        'Wage Amount',
                                        wageAmountAuditHelp
                                    )}
                                    <FormControl
                                        type='number'
                                        id='reimbursementAmount'
                                        name='reimbursementAmount'
                                        value={reimbursementAmount}
                                        onChange={handleInputChange}
                                        className='audit-field-control'
                                    />
                                </div>
                            </div>
                            <div className='audit-adjustments-overrides'>
                                <div className='audit-adjustments-subhead'>
                                    Policy Overrides
                                </div>
                                <div className='audit-adjustments-override-chips'>
                                    {renderPolicyOverrideChip(
                                        ninetyDayLWROverride,
                                        () => setNinetyDayLWROverride(v => !v),
                                        '90-day wages',
                                        'popover-90-day-lwr',
                                        '90-day wages window',
                                        'Allow wage reimbursement requests up to 90 days after donation.'
                                    )}
                                    {renderPolicyOverrideChip(
                                        sixtyDayOverride,
                                        () => setSixtyDayOverride(v => !v),
                                        '60-day travel',
                                        'popover-60-day-der',
                                        '60-day travel expenses window',
                                        'Allow travel expense reimbursement requests up to 60 days after donation.'
                                    )}
                                    {renderPolicyOverrideChip(
                                        expenseLimitOverride,
                                        () => setExpenseLimitOverride(v => !v),
                                        'Multi DER',
                                        'popover-multi-der',
                                        'Multiple travel expense requests',
                                        'Allow more than one travel expense reimbursement request.'
                                    )}
                                </div>
                            </div>
                            {hasPendingAuditChanges &&
                                <div className='audit-pending-changes-notice' role='status'>
                                    Pending changes — click Update to save to this request.
                                </div>
                            }
                            <Button
                                bsStyle='primary'
                                className='audit-update-btn audit-update-btn-full'
                                onClick={() => handleAuditUpdate()}>
                                Update
                            </Button>
                            <p className='audit-adjustments-save-note'>
                                <strong>Update</strong> saves Request Type, Donation Date, Status, Wage Amount, and Policy Overrides above.
                                <span className='audit-adjustments-save-note-muted'> Wage Amount does not affect travel. $0 is usually pending verify — change only to fix bad data.</span>
                            </p>
                        </Panel.Body>
                    </Panel>
                }
            </div>

            {isAuditAndNkrVerifyTax &&
                <>
                    {/* <Row>
                        <Col sm={12}>
                            <FormGroup>
                                <Checkbox
                                    checked={sendNotice}
                                    onClick={() => setSendNotice(!sendNotice)}
                                    style={{ textAlign: 'left', marginRight: '20px', marginTop: '0px', marginBottom: '0px' }}>
                                    Send Approve/Decline Notification to Donor
                                </Checkbox>
                            </FormGroup>
                        </Col>
                    </Row> */}
                    <Row>
                        <Col sm={props.data.watrRequestType == WageAndTravelType.ReimburseBoth ? 3 : 4}>
                            <Button
                                bsStyle="danger"
                                style={{ width: '100%', paddingLeft: '5px', paddingRight: '5px' }}
                                onClick={() => context.onShowDeclineModal(props.data.id, false)}>Decline</Button>
                        </Col>

                        {props.data.watrRequestType == WageAndTravelType.ReimburseBoth &&
                            <Col sm={3}>
                                <Button
                                    bsStyle="warning"
                                    style={{ width: '100%', paddingLeft: '5px', paddingRight: '5px' }}
                                    onClick={() => handleVerifyAmount(WageAndTravelStatus.PendingTravelReceipts, false)}>Decline Wages Only</Button>
                            </Col>
                        }

                        <Col sm={props.data.watrRequestType == WageAndTravelType.ReimburseBoth ? 3 : 4}>
                            <Button
                                bsStyle="success"
                                style={{ width: '100%', paddingLeft: '5px', paddingRight: '5px' }}
                                onClick={() => handleVerifyAmount(WageAndTravelStatus.PendingSurgery, false)}>Approve</Button>
                        </Col>
                        
                        <Col sm={props.data.watrRequestType == WageAndTravelType.ReimburseBoth ? 3 : 4}>
                            <Button
                                bsStyle="success"
                                style={{ width: '100%', paddingLeft: '5px', paddingRight: '5px' }}
                                onClick={() => handleVerifyAmount(WageAndTravelStatus.PendingSurgery, true)}>Approve - Modify Email</Button>
                        </Col>
                    </Row>
                    <hr />
                </>
            }

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
                paymentWorkflow={props.isAudit ? undefined : 'wages'}
                reimbursementType={props.data.watrRequestType}
                allowEditWeekOtherAmount={canEditOtherAmount}
                onWeekOtherAmountChange={() => {}}
                onSaveWeekOtherAmount={canEditOtherAmount ? handleSaveWeekOtherAmount : undefined}
                savingWeekRequested={savingWeekRequested}
                saveError={saveError}
            />

            <Modal
                backdrop='static'
                show={showTerminalStatusConfirm}
                onHide={() => setShowTerminalStatusConfirm(false)}
                className='modal-vertical-center'
                style={{ display: 'flex', alignItems: 'center' }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm status update</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '28px 32px' }}>
                    <p style={{ marginBottom: 20, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5, color: '#2f3a46' }}>
                        You have requested to update the status to{' '}
                        <strong>
                            {context.statusTypes.find(x => x.id === status)?.description
                                ?? WageAndTravelStatus[status]
                                ?? status}
                        </strong>.
                        {' '}
                        If you {status === WageAndTravelStatus.RequestCancelled
                            ? 'cancel'
                            : status === WageAndTravelStatus.Denied
                                ? 'deny'
                                : status === WageAndTravelStatus.Expired
                                    ? 'expire'
                                    : 'update'} this request, the following changes will also occur automatically.
                    </p>
                    <p style={{ marginBottom: 0, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5, color: '#2f3a46' }}>
                        Pending additional week{pendingAdditionalWeeks.length === 1 ? '' : 's'}{' '}
                        <strong>{pendingAdditionalWeeks.join(', ')}</strong>{' '}
                        will be automatically declined.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='danger' onClick={submitAuditUpdate}>Yes, Save and Decline Weeks</Button>
                    <Button onClick={() => setShowTerminalStatusConfirm(false)}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
    // }
}

export default ReimburseWages;